import { LocationType, type MapPoint } from '../types';
import { CITY_ZOOM, MAX_ZOOM, METRO_ZOOM, MIN_ZOOM } from '../utils/constants';

export interface ChoroplethLayerConfig {
  id: LocationType;
  sourceId: string;
  fillLayerId: string;
  outlineLayerId: string;
  sourceType: 'vector' | 'geojson';
  sourceUrl?: string;
  sourceLayer?: string;
  propertyName: string;
  promoteId?: string;
  isApplicable: (point: MapPoint) => boolean;
  minzoom?: number;
  maxzoom?: number;
}

type ChoroplethSource = Pick<
  ChoroplethLayerConfig,
  'sourceType' | 'sourceUrl' | 'sourceLayer' | 'promoteId'
>;

type ChoroplethLayerDefinition = Omit<
  ChoroplethLayerConfig,
  keyof ChoroplethSource
>;

const LAYER_DEFINITIONS: ChoroplethLayerDefinition[] = [
  {
    id: LocationType.REGION,
    sourceId: 'regions_source',
    fillLayerId: 'regions_choropleth_fill',
    outlineLayerId: 'regions_choropleth_outline',
    propertyName: 'name',
    minzoom: MIN_ZOOM,
    maxzoom: METRO_ZOOM,
    isApplicable: (point: MapPoint) =>
      point.locationType === LocationType.REGION,
  },
  {
    id: LocationType.COUNTRY,
    sourceId: 'countries_source',
    fillLayerId: 'country_choropleth_fill',
    outlineLayerId: 'country_choropleth_outline',
    propertyName: 'iso_3166_1',
    minzoom: MIN_ZOOM,
    maxzoom: METRO_ZOOM,
    isApplicable: (point: MapPoint) =>
      point.locationType === LocationType.COUNTRY,
  },
  {
    id: LocationType.METRO_AREA,
    sourceId: 'metro_areas_source',
    fillLayerId: 'metro_areas_choropleth_fill',
    outlineLayerId: 'metro_areas_choropleth_outline',
    propertyName: 'msa_id',
    minzoom: METRO_ZOOM,
    maxzoom: CITY_ZOOM,
    isApplicable: (point: MapPoint) =>
      point.locationType === LocationType.METRO_AREA,
  },
  {
    id: LocationType.STATE,
    sourceId: 'states_source',
    fillLayerId: 'states_choropleth_fill',
    outlineLayerId: 'states_choropleth_outline',
    propertyName: 'state_id',
    minzoom: METRO_ZOOM,
    maxzoom: CITY_ZOOM,
    isApplicable: (point: MapPoint) =>
      point.locationType === LocationType.STATE,
  },
  {
    id: LocationType.COUNTY,
    sourceId: 'counties_source',
    fillLayerId: 'counties_choropleth_fill',
    outlineLayerId: 'counties_choropleth_outline',
    propertyName: 'county_id',
    minzoom: CITY_ZOOM,
    maxzoom: MAX_ZOOM,
    isApplicable: (point: MapPoint) =>
      point.locationType === LocationType.COUNTY,
  },
  {
    id: LocationType.CITY,
    sourceId: 'cities_source',
    fillLayerId: 'cities_choropleth_fill',
    outlineLayerId: 'cities_choropleth_outline',
    propertyName: 'city_id',
    minzoom: CITY_ZOOM,
    maxzoom: MAX_ZOOM,
    isApplicable: (point: MapPoint) => point.locationType === LocationType.CITY,
  },
];

const SELF_HOSTED_TILES_BASE_URL = 'https://assets.reveliolabs.com/tiles';

const pmtilesUrl = (fileName: string): string =>
  `pmtiles://${SELF_HOSTED_TILES_BASE_URL}/${fileName}.pmtiles`;

// Countries render from a bundled Natural Earth 50m file whose iso_3166_1
// property matches the joins in choroplethFeatureBuilder and
// paintExpressionBuilder.
const LAYER_SOURCES: Partial<Record<LocationType, ChoroplethSource>> = {
  [LocationType.REGION]: {
    sourceType: 'vector',
    sourceUrl: pmtilesUrl('region-borders'),
    sourceLayer: 'regionBorders',
  },
  [LocationType.COUNTRY]: {
    sourceType: 'geojson',
    sourceUrl: '/assets/map/ne-countries-50m.geojson.json',
    promoteId: 'iso_3166_1',
  },
  [LocationType.METRO_AREA]: {
    sourceType: 'vector',
    sourceUrl: pmtilesUrl('metro-non-metro-area-borders-v2'),
    sourceLayer: 'metroNonMetroAreaBorders',
  },
  [LocationType.STATE]: {
    sourceType: 'vector',
    sourceUrl: pmtilesUrl('state-borders-v4'),
    sourceLayer: 'adm1',
  },
  [LocationType.COUNTY]: {
    sourceType: 'vector',
    sourceUrl: pmtilesUrl('county-borders-v5'),
    sourceLayer: 'adm2',
  },
  [LocationType.CITY]: {
    sourceType: 'vector',
    sourceUrl: pmtilesUrl('city-borders-v6'),
    sourceLayer: 'cityBorders_v10-3h299t',
  },
};

export const LAYER_CONFIGS: ChoroplethLayerConfig[] = LAYER_DEFINITIONS.flatMap(
  (definition) => {
    const source = LAYER_SOURCES[definition.id];
    return source ? [{ ...definition, ...source }] : [];
  }
);
